import React, { useState, useEffect, useRef } from "react";
import accountingReportsApi from "../../api/accountingReportsApi";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import {
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import ReportPrintHeader from "../../components/ReportPrintHeader.jsx";

export default function TrialBalanceReport() {
  const [fromDate, setFromDate] = useState(
    new Date().getFullYear() + "-01-01"
  );
  // ... rest of state ...
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState({
    opDebit: 0, opCredit: 0,
    transDebit: 0, transCredit: 0,
    netDebit: 0, netCredit: 0
  });

  const printRef = useRef();

  const handleSearch = async () => {
    setLoading(true);
    try {
      const res = await accountingReportsApi.getTrialBalance({
        fromDate,
        toDate,
      });
      setReportData(res.data);
      calculateTotals(res.data);
    } catch (error) {
      console.error(error);
      toast.error("فشل جلب ميزان المراجعة");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (data) => {
    const t = data.reduce((acc, curr) => ({
      opDebit: acc.opDebit + curr.openingDebit,
      opCredit: acc.opCredit + curr.openingCredit,
      transDebit: acc.transDebit + curr.transactionDebit,
      transCredit: acc.transCredit + curr.transactionCredit,
      netDebit: acc.netDebit + curr.netDebit,
      netCredit: acc.netCredit + curr.netCredit,
    }), { opDebit: 0, opCredit: 0, transDebit: 0, transCredit: 0, netDebit: 0, netCredit: 0 });
    setTotals(t);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `TrialBalance-${new Date().toISOString()}`,
  });

  return (
    <Box p={3} dir="rtl">
      <Typography variant="h4" fontWeight="bold" mb={3} color="primary">
        ⚖️ ميزان المراجعة (Trial Balance)
      </Typography>

      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="من تاريخ"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="إلى تاريخ"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2} display="flex" gap={1}>
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                fullWidth
                sx={{ height: 56 }}
              >
                {loading ? "..." : "بحث"}
              </Button>
              {reportData.length > 0 && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handlePrint}
                  sx={{ height: 56, minWidth: 56 }}
                >
                  <PrintIcon />
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {reportData.length > 0 && (
        <div ref={printRef} style={{ padding: "20px", backgroundColor: "white" }}>
          
          <Box>
             <ReportPrintHeader 
                title="ميزان المراجعة"
                dateRange={`من ${new Date(fromDate).toLocaleDateString("en-GB")} إلى ${new Date(toDate).toLocaleDateString("en-GB")}`}
             />
             <style>{`
                @media print {
                   .no-print { display: none !important; }
                   /* Ensure table header backgrounds print */
                   thead { background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; }
                   th { background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; }
                }
             `}</style>
          </Box>

          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
            <Table sx={{ minWidth: 800 }} size="small">
              <TableHead sx={{ bgcolor: "#eeeeee" }}>
                <TableRow>
                  <TableCell rowSpan={2} align="center" sx={{ fontWeight: "bold", borderRight: "1px solid #ddd" }}>رقم الحساب</TableCell>
                  <TableCell rowSpan={2} align="left" sx={{ fontWeight: "bold", borderRight: "1px solid #ddd" }}>اسم الحساب</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: "bold", borderBottom: "1px solid #ddd", bgcolor: "#e3f2fd" }}>رصيد افتتاحي</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: "bold", borderBottom: "1px solid #ddd", bgcolor: "#fff3e0" }}>حركة الفترة</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: "bold", borderBottom: "1px solid #ddd", bgcolor: "#e8f5e9" }}>رصيد ختامي</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#2e7d32", bgcolor: "#e3f2fd" }}>مدين</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#c62828", bgcolor: "#e3f2fd" }}>دائن</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#2e7d32", bgcolor: "#fff3e0" }}>مدين</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#c62828", bgcolor: "#fff3e0" }}>دائن</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#2e7d32", bgcolor: "#e8f5e9" }}>مدين</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#c62828", bgcolor: "#e8f5e9" }}>دائن</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((row) => (
                  <TableRow key={row.accountId} hover>
                    <TableCell align="center">{row.accountCode}</TableCell>
                    <TableCell align="left">{row.accountName}</TableCell>
                    
                    {/* Opening */}
                    <TableCell align="center">{row.openingDebit > 0 ? row.openingDebit.toLocaleString() : "-"}</TableCell>
                    <TableCell align="center">{row.openingCredit > 0 ? row.openingCredit.toLocaleString() : "-"}</TableCell>
                    
                    {/* Transactions */}
                    <TableCell align="center">{row.transactionDebit > 0 ? row.transactionDebit.toLocaleString() : "-"}</TableCell>
                    <TableCell align="center">{row.transactionCredit > 0 ? row.transactionCredit.toLocaleString() : "-"}</TableCell>
                    
                    {/* Closing */}
                    <TableCell align="center" sx={{ fontWeight: "bold", color: "#1b5e20" }}>
                      {row.netDebit > 0 ? row.netDebit.toLocaleString() : "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", color: "#b71c1c" }}>
                      {row.netCredit > 0 ? row.netCredit.toLocaleString() : "-"}
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Totals Row */}
                <TableRow sx={{ bgcolor: "#212121" }}>
                  <TableCell colSpan={2} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.1rem" }}>الإجمـــالي</TableCell>
                  <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>{totals.opDebit.toLocaleString()}</TableCell>
                  <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>{totals.opCredit.toLocaleString()}</TableCell>
                  <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>{totals.transDebit.toLocaleString()}</TableCell>
                  <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>{totals.transCredit.toLocaleString()}</TableCell>
                  <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>{totals.netDebit.toLocaleString()}</TableCell>
                  <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>{totals.netCredit.toLocaleString()}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </Box>
  );
}
